import React, { useState, useEffect } from 'react';
import {
  Check, ChevronRight, ChevronDown, Sparkles, Loader2,
  FolderPlus, Plus, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

/**
 * Category selector with AI-suggested categories.
 * When a URL is present, shows two sections:
 *   1. "Suggested Categories" - only categories whose IDs are in suggestedIds
 *      PLUS an "+ Add New" chip that expands the new-category panel below
 *   2. "More Categories" - expandable section with ALL categories (suggested ones pre-checked)
 *
 * The new-category panel calls POST /api/categories/suggest-new lazily on
 * first expand (cached afterwards) and POST /api/categories per pick.
 * Newly-created categories are bubbled up via `onCategoryCreated` so the
 * parent can keep its shared `categories` array in sync.
 */
export function CategoryPicker({
  categories, selectedIds, onToggle, getCategoryIcon,
  suggestedIds = [], loading = false, hasUrl = false,
  testId = 'category-select',
  // Context for the AI to generate new-category suggestions. At least
  // one of these should be present for the panel to be useful.
  bookmarkContext = null, // { url, title, summary, bookmark_id }
  onCategoryCreated = null,
}) {
  const [showAll, setShowAll] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const parents = categories.filter(c => !c.parent_id);
  const hasSuggestions = suggestedIds.length > 0;

  const renderCategoryButton = (cat, isSub = false) => {
    const IconComponent = getCategoryIcon(cat.icon);
    const isSelected = selectedIds.includes(cat.category_id);
    return (
      <button
        key={cat.category_id}
        type="button"
        onClick={() => onToggle(cat.category_id)}
        className={`inline-flex items-center gap-1 ${isSub ? 'px-2 py-1' : 'px-2.5 py-1'} rounded-lg text-xs ${isSub ? '' : 'font-medium'} transition-all border ${
          isSelected
            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
            : 'bg-transparent text-muted-foreground border-border hover:bg-secondary/50'
        }`}
        data-testid={`cat-${cat.category_id}`}
      >
        {isSelected && <Check className="w-3 h-3" />}
        {isSub && <ChevronRight className="w-2.5 h-2.5" />}
        <IconComponent className="w-3 h-3" style={{ color: isSelected ? 'currentColor' : cat.color }} />
        {cat.name}
      </button>
    );
  };

  const renderParentWithSubs = (cat) => {
    const subs = categories.filter(s => s.parent_id === cat.category_id);
    return (
      <React.Fragment key={cat.category_id}>
        {renderCategoryButton(cat)}
        {subs.map(sub => renderCategoryButton(sub, true))}
      </React.Fragment>
    );
  };

  // The "+ Add New" chip - sits at the end of the Suggested Categories
  // row. Tapping it toggles the NewCategoryPanel below.
  const renderAddNewChip = () => (
    <button
      key="add-new-chip"
      type="button"
      onClick={() => setNewOpen(v => !v)}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border border-dashed ${
        newOpen
          ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
          : 'bg-transparent text-muted-foreground border-border hover:bg-secondary/50 hover:text-emerald-700'
      }`}
      data-testid="add-new-category-chip"
      aria-expanded={newOpen}
    >
      <FolderPlus className="w-3 h-3" />
      Add New
      {newOpen ? <ChevronDown className="w-2.5 h-2.5" /> : <Plus className="w-2.5 h-2.5" />}
    </button>
  );

  const newPanel = newOpen ? (
    <NewCategoryPanel
      bookmarkContext={bookmarkContext}
      existingCategories={categories}
      getCategoryIcon={getCategoryIcon}
      onCategoryCreated={(cat) => {
        // Bubble up so the parent's `categories` array stays the
        // source of truth, then auto-select the new one for THIS
        // bookmark.
        onCategoryCreated?.(cat);
        onToggle?.(cat.category_id);
      }}
    />
  ) : null;

  // URL mode: show suggested + expandable full list
  if (hasUrl) {
    const suggestedCats = hasSuggestions
      ? categories.filter(c => suggestedIds.includes(c.category_id))
      : [];

    // No AI match? Hide the "Suggested" label but STILL show the
    // "+ Add New" affordance - adding a fresh category is the most
    // useful thing when no existing one fits.
    if (!loading && !hasSuggestions) {
      return (
        <div>
          <div className="flex flex-wrap gap-1.5 p-2 border rounded-xl" data-testid={testId}>
            {renderAddNewChip()}
          </div>
          {newPanel}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="text-xs text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors"
              data-testid="more-categories-toggle"
            >
              {showAll ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {showAll ? 'Hide collections' : 'Pick a collection (optional)'}
            </button>
            {showAll && (
              <div className="flex flex-wrap gap-1.5 p-2 border rounded-xl mt-1.5 animate-in fade-in-0 slide-in-from-top-1 duration-200" data-testid="more-categories-list">
                {parents.map(p => renderParentWithSubs(p))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div>
        <label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          Suggested collections
          {loading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
        </label>
        <div className="flex flex-wrap gap-1.5 p-2 border rounded-xl min-h-[38px]" data-testid={testId}>
          {loading && !hasSuggestions && (
            <span className="text-xs text-muted-foreground py-0.5">Analyzing URL...</span>
          )}
          {suggestedCats.map(cat => renderCategoryButton(cat, !!cat.parent_id))}
          {renderAddNewChip()}
        </div>

        {newPanel}

        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-xs text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors"
            data-testid="more-categories-toggle"
          >
            {showAll ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {showAll ? 'Hide collections' : 'More collections'}
          </button>
          {showAll && (
            <div className="flex flex-wrap gap-1.5 p-2 border rounded-xl mt-1.5 animate-in fade-in-0 slide-in-from-top-1 duration-200" data-testid="more-categories-list">
              {parents.map(p => renderParentWithSubs(p))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // No URL mode (text/voice/manual save): flat list + "+ Add New"
  return (
    <div>
      <label className="text-sm font-medium mb-1.5 block">
        Collections <span className="text-muted-foreground font-normal">(optional)</span>
      </label>
      <div className="flex flex-wrap gap-1.5 p-2 border rounded-xl min-h-[38px]" data-testid={testId}>
        {parents.map(p => renderParentWithSubs(p))}
        {renderAddNewChip()}
        {categories.length === 0 && (
          <span className="text-xs text-muted-foreground py-0.5">No collections yet</span>
        )}
      </div>
      {newPanel}
    </div>
  );
}


/**
 * NewCategoryPanel - inline expansion below the Suggested Categories row.
 *
 * Behaviour:
 *   - Lazily calls /api/categories/suggest-new on first mount and caches
 *     the result (per panel instance, so re-collapsing doesn't re-fetch).
 *   - Renders up to 3 AI-proposed NEW categories as chips. Tap to create
 *     + auto-select for the current bookmark. Hard cap of 3 selections.
 *   - Bottom row: free-text input + "Create" button to coin a custom
 *     category that the AI didn't suggest.
 */
function NewCategoryPanel({ bookmarkContext, existingCategories, getCategoryIcon, onCategoryCreated }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skipped, setSkipped] = useState(null);
  // Track which suggestions the user has TAKEN (created already).
  const [takenNames, setTakenNames] = useState(new Set());
  const [customName, setCustomName] = useState('');
  const [creatingCustom, setCreatingCustom] = useState(false);

  // ── Pull suggestions once on mount.
  useEffect(() => {
    let cancelled = false;
    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/categories/suggest-new`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(bookmarkContext || {}),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        setSkipped(data.skipped_reason || null);
      } catch (e) {
        if (!cancelled) {
          // Silent: the custom-name input still works without AI.
          setSuggestions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSuggestions();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createCategory = async ({ name, icon, color }) => {
    const res = await fetch(`${API}/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, icon: icon || 'folder', color: color || '#8b5cf6' }),
    });
    if (!res.ok) {
      throw new Error(`Could not create "${name}" (HTTP ${res.status})`);
    }
    return res.json();
  };

  const handleSuggestionPick = async (s) => {
    if (takenNames.has(s.name)) return;
    if (takenNames.size >= 3) {
      toast.info('Up to 3 new collections at a time');
      return;
    }
    try {
      const created = await createCategory(s);
      setTakenNames(prev => {
        const next = new Set(prev);
        next.add(s.name);
        return next;
      });
      onCategoryCreated?.(created);
      toast.success(`Created "${created.name}"`);
    } catch (e) {
      toast.error(e.message || 'Could not create collection');
    }
  };

  const handleCreateCustom = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    const name = customName.trim();
    if (!name) return;
    if (name.length > 24) {
      toast.error('Collection name must be 24 characters or fewer');
      return;
    }
    // Prevent dupes against existing categories (case-insensitive).
    const dupe = existingCategories.find(c => (c.name || '').toLowerCase() === name.toLowerCase());
    if (dupe) {
      toast.info(`"${dupe.name}" already exists - adding it instead`);
      onCategoryCreated?.(dupe);
      setCustomName('');
      return;
    }
    setCreatingCustom(true);
    try {
      const created = await createCategory({ name, icon: 'folder', color: '#8b5cf6' });
      // Stamp the "taken" pill so the user sees confirmation that the
      // category was created AND auto-attached to the bookmark - prevents
      // the user from thinking nothing happened and re-clicking Create.
      setTakenNames(prev => {
        const next = new Set(prev);
        next.add(created.name);
        return next;
      });
      onCategoryCreated?.(created);
      setCustomName('');
      toast.success(`Created "${created.name}"`);
    } catch (err) {
      toast.error(err.message || 'Could not create collection');
    } finally {
      setCreatingCustom(false);
    }
  };

  const visibleSuggestions = suggestions.filter(s => !takenNames.has(s.name));
  const remainingSlots = 3 - takenNames.size;

  return (
    <div
      className="mt-2 rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 animate-in fade-in-0 slide-in-from-top-1 duration-200"
      data-testid="new-category-panel"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
        <span className="text-xs font-semibold text-emerald-900">New collections for this bookmark</span>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2.5">
        {skipped === 'category_limit'
          ? 'You already have a rich collection library - try a custom name instead.'
          : `Pick up to 3 - we'll add them to your library${remainingSlots < 3 ? ` (${remainingSlots} left)` : ''}.`}
      </p>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-1.5" data-testid="new-category-loading">
          <Loader2 className="w-3 h-3 animate-spin" />
          Coming up with ideas…
        </div>
      )}

      {!loading && visibleSuggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2.5" data-testid="new-category-suggestions">
          {visibleSuggestions.map((s) => {
            const Icon = getCategoryIcon(s.icon);
            const disabled = remainingSlots <= 0;
            return (
              <button
                key={s.name}
                type="button"
                disabled={disabled}
                onClick={() => handleSuggestionPick(s)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                  disabled
                    ? 'bg-secondary/40 text-muted-foreground border-border opacity-60 cursor-not-allowed'
                    : 'bg-white text-zinc-800 border-zinc-200 hover:border-emerald-300 hover:bg-emerald-50'
                }`}
                title={s.rationale || ''}
                data-testid={`new-category-suggestion-${s.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-3 h-3" style={{ color: s.color }} />
                {s.name}
                <Plus className="w-2.5 h-2.5 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      )}

      {!loading && takenNames.size > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2.5">
          {[...takenNames].map(name => (
            <span
              key={name}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-600 text-white"
              data-testid={`new-category-taken-${name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Check className="w-2.5 h-2.5" />
              {name}
            </span>
          ))}
        </div>
      )}

      {!loading && visibleSuggestions.length === 0 && takenNames.size === 0 && (
        <p className="text-[11px] text-muted-foreground italic mb-2.5">
          No automatic suggestions - create your own below.
        </p>
      )}

      <div className="flex gap-1.5 items-stretch" data-testid="new-category-custom-row">
        <div className="relative flex-1">
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            onKeyDown={(e) => {
              // Pressing Enter triggers Create. We deliberately do
              // NOT wrap this in a <form> because the parent
              // AddBookmarkModal already has an outer <form> - nested
              // forms get flattened by the browser, causing the
              // outer form's submit to fire instead of our handler.
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                handleCreateCustom();
              }
            }}
            placeholder="Or create your own…"
            maxLength={24}
            className="w-full h-8 px-2.5 pr-7 text-xs rounded-lg border border-zinc-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300"
            data-testid="new-category-custom-input"
          />
          {customName.length > 0 && (
            <button
              type="button"
              onClick={() => setCustomName('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              aria-label="Clear"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleCreateCustom(); }}
          disabled={!customName.trim() || creatingCustom}
          className="h-8 px-3 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="new-category-custom-submit"
        >
          {creatingCustom ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create'}
        </button>
      </div>
    </div>
  );
}
